import { useMutation } from "@tanstack/react-query";
import { updateWorkoutItemAttributes } from "api-client";
import { observer } from "mobx-react-lite";
import { useRef, useState } from "react";
import { Checkbox } from "ui";
import { useDebouncedCallback } from "use-debounce";

import {
  type WorkoutItemAttributePickerRenderAttributeProps,
  WorkoutItemAttributePicker,
} from "src/components/WorkoutItemAttributePicker";
import { useAuthenticatedNavigationContext } from "src/layout/AuthenticatedNavigationContext";

import { useWorkoutItemMutationScope } from "./useWorkoutItemMutationScope";
import { useWorkoutEditStore } from "./WorkoutEditContext";
import { useWorkoutPermissions } from "./WorkoutItemsPermission";

export interface WorkoutItemAttributeSelectionProps {
  workoutItemId: string;
}

interface UpdateWorkoutItemAttributesMutationInput {
  exerciseAttributeIds: string[];
  latestUpdateTick: number;
}

export const WorkoutItemAttributeSelection = observer(
  function WorkoutItemAttributeSelection(
    props: WorkoutItemAttributeSelectionProps,
  ) {
    const workoutEditStore = useWorkoutEditStore();
    const workoutItemsPermissions = useWorkoutPermissions();
    const workoutItemAttributes = workoutEditStore.getWorkoutItemAttributes(
      props.workoutItemId,
    );
    const [draftSelectedAttributeIds, setDraftSelectedAttributeIds] = useState<
      string[] | null
    >(null);
    const latestUpdateTickRef = useRef(0);
    const scope = useWorkoutItemMutationScope(props.workoutItemId);
    const { withSaving } = useAuthenticatedNavigationContext();

    const selectedWorkoutItemAttributeIds = workoutItemAttributes.map(
      (attribute) => {
        return attribute.exerciseAttributeId;
      },
    );

    const selectedAttributeIds =
      draftSelectedAttributeIds ?? selectedWorkoutItemAttributeIds;

    const mutation = useMutation({
      scope,
      mutationFn: async (ctx: UpdateWorkoutItemAttributesMutationInput) => {
        const response = await withSaving(
          updateWorkoutItemAttributes({
            path: {
              workoutId: workoutEditStore.workout.id,
              workoutItemId: props.workoutItemId,
            },
            body: { exerciseAttributeIds: ctx.exerciseAttributeIds },
          }),
        );

        return {
          data: response.data.data,
          latestUpdateTick: ctx.latestUpdateTick,
        };
      },
      onSuccess: (response) => {
        if (latestUpdateTickRef.current !== response.latestUpdateTick) {
          return;
        }

        workoutEditStore.updateWorkoutItemAttributes(response.data);
        setDraftSelectedAttributeIds(null);
      },
      onError: (_error, variables) => {
        if (latestUpdateTickRef.current !== variables.latestUpdateTick) {
          return;
        }

        setDraftSelectedAttributeIds(null);
      },
    });

    const debouncedMutate = useDebouncedCallback(mutation.mutate, 150);

    function updateAttributes(exerciseAttributeId: string, checked: boolean) {
      const selected = selectedAttributeIds.includes(exerciseAttributeId);

      if (checked && selected) {
        return;
      }

      if (!checked && !selected) {
        return;
      }

      const nextAttributeIds = checked
        ? [...selectedAttributeIds, exerciseAttributeId]
        : selectedAttributeIds.filter((selectedAttributeId) => {
            return selectedAttributeId !== exerciseAttributeId;
          });

      const latestUpdateTick = latestUpdateTickRef.current + 1;
      latestUpdateTickRef.current = latestUpdateTick;
      setDraftSelectedAttributeIds(nextAttributeIds);
      debouncedMutate({
        exerciseAttributeIds: nextAttributeIds,
        latestUpdateTick,
      });
    }

    function renderAttributeOption(
      renderProps: WorkoutItemAttributePickerRenderAttributeProps,
    ) {
      const selected = selectedAttributeIds.includes(renderProps.attribute.id);
      return (
        <>
          <Checkbox
            aria-label={renderProps.label}
            checked={selected}
            disabled={renderProps.disabled}
            onClick={(event) => {
              event.stopPropagation();
              return;
            }}
            onCheckedChange={(checked) => {
              updateAttributes(renderProps.attribute.id, checked === true);
              return;
            }}
          />
          <span className="flex-1">{renderProps.label}</span>
        </>
      );
    }

    return (
      <WorkoutItemAttributePicker
        selectedAttributeIds={selectedAttributeIds}
        disabled={!workoutItemsPermissions.canEditItems}
        renderAttribute={renderAttributeOption}
        onCheckedChange={(exerciseAttributeId, checked) => {
          updateAttributes(exerciseAttributeId, checked);
          return;
        }}
      />
    );
  },
);
